import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import API_URL from '../config/api';
import { format, addDays, startOfWeek, addWeeks } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { getTimezoneFromAddress } from '../{components,pages,hooks,utils,styles}/constants/constants.js';
import '../styles/BookingCalendar.css';

function BookingCalendar({ leadInfo, onBookingComplete }) {
  const [step, setStep] = useState(1);
  const [calendars, setCalendars] = useState([]);
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [customerTimezone, setCustomerTimezone] = useState('America/Denver');

  useEffect(() => {
    loadCalendars();
    if(leadInfo.address){
        const tz = getTimezoneFromAddress(leadInfo.address);
        setCustomerTimezone(tz)
    }
  }, []);

  useEffect(() => {
    if (selectedCalendar && selectedDate) {
      loadAvailableSlots();
    }
  }, [selectedCalendar, selectedDate]);

  const loadCalendars = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/availability/calendars`);
      setCalendars(response.data.calendars || []);
    } catch (error) {
      console.error('Error loading calendars:', error);
      alert('Failed to load calendars');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableSlots = async () => {
    setLoading(true);
    try {
        console.log('Loading slots with timezone:', customerTimezone);
      const response = await axios.get(`${API_URL}/api/availability/aggregated-slots`, {
        params: {
          calendarId: selectedCalendar.id,
          date: format(selectedDate, 'yyyy-MM-dd'),
          timezone: customerTimezone
        }
      });
      setAvailableSlots(response.data.slots || []);
    } catch (error) {
      console.error('Error loading time slots:', error);
      alert('Failed to load available times');
    } finally {
      setLoading(false);
    }
  };

  const handleCalendarSelect = (calendar) => {
    setSelectedCalendar(calendar);
    setSelectedDate(null);
    setSelectedSlot(null);
    setStep(2);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
  };

  const handleBooking = async () => {
    if (!selectedSlot) return;

    setLoading(true);
    try {
      const bookingData = {
        calendarId: selectedCalendar.id,
        calendarTitle: selectedCalendar.title,
        slot: selectedSlot,
        customerTimezone: customerTimezone,
        contact: {
          name: leadInfo.name,
          email: leadInfo.email,
          phone: leadInfo.phone,
          address: leadInfo.address
        }
      };

      const response = await axios.post(`${API_URL}/api/availability/book`, bookingData);

      onBookingComplete({
        ...bookingData,
        appointmentId: response.data.appointmentId,
        message: response.data.message
      });
    } catch (error) {
      console.error('Booking error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to book appointment. Please try again.';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeInCustomerTz = (dateString) => {
    const date = new Date(dateString);
    return formatInTimeZone(date, customerTimezone, 'h:mm a')
  };

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  if (loading && calendars.length === 0 && step === 1) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading calendars...</p>
      </div>
    );
  }

  return (
    <div className="booking-calendar">
      {/* Step Indicator */}
      <div className="booking-steps">
        <div className={`step ${step >= 1 ? 'active' : ''}`}>
          <div className="step-number">1</div>
          <div className="step-label">Choose Calendar</div>
        </div>
        <div className="step-line"></div>
        <div className={`step ${step >= 2 ? 'active' : ''}`}>
          <div className="step-number">2</div>
          <div className="step-label">Select Time</div>
        </div>
      </div>

      {/* Step 1: Calendar Selection */}
      {step === 1 && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="calendar-selection"
        >
          <h2>Choose a Calendar</h2>
          <p className="subtitle">Select the team calendar for your energy audit</p>
          <div className="calendar-grid">
            {calendars.map((calendar) => (
              <motion.div
                key={calendar.id}
                className="calendar-card"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleCalendarSelect(calendar)}
              >
                <div 
                  className="calendar-color-bar" 
                  style={{ backgroundColor: calendar.calendarColour }}
                ></div>
                <div className="calendar-content">
                  <h3>{calendar.title}</h3>
                  <p className="calendar-description">{calendar.description}</p>
                  <div className="calendar-meta">
                    <span className="calendar-type">{calendar.calendarTypeDescription}</span>
                    <span className="calendar-duration">
                      {calendar.durationTime} {calendar.durationType}
                    </span>
                  </div>
                  {calendar.timezone && (
                    <span className="calendar-timezone">🌍 {calendar.timezone}</span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Step 2: Date & Time Selection */}
      {step === 2 && (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="datetime-selection"
        >
          <div className="booking-header">
            <button onClick={() => setStep(1)} className="back-button">
              ← Back to Calendars
            </button>
            <div className="selected-calendar-info">
              <h3>{selectedCalendar.title}</h3>
              <p>Select your preferred date and time</p>
            </div>
          </div>

          {/* Week Navigator */}
          <div className="week-navigator">
            <button 
              onClick={() => setWeekStart(addWeeks(weekStart, -1))}
              className="nav-button"
            >
              ←
            </button>
            <span className="week-label">
              {format(weekStart, 'MMM d')} - {format(addDays(weekStart, 6), 'MMM d, yyyy')}
            </span>
            <button 
              onClick={() => setWeekStart(addWeeks(weekStart, 1))}
              className="nav-button"
            >
              →
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="calendar-days-grid">
            {weekDays.map((day) => (
              <motion.div
                key={day.toISOString()}
                className={`calendar-day ${selectedDate?.toDateString() === day.toDateString() ? 'selected' : ''}`}
                onClick={() => handleDateSelect(day)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="day-name">{format(day, 'EEE')}</div>
                <div className="day-number">{format(day, 'd')}</div>
              </motion.div>
            ))}
          </div>

          {/* Time Slots */}
          {selectedDate && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="time-slots"
            >
              <h3>Available Times for {format(selectedDate, 'MMMM d, yyyy')}</h3>
              {loading ? (
                <div className="loading-slots">Loading available times...</div>
              ) : availableSlots.length === 0 ? (
                <p className="no-slots">No available times for this date. Please select another day.</p>
              ) : (
                <>
                  <div className="slots-grid">
                    {availableSlots.map((slot, index) => (
                      <motion.button
                        key={index}
                        className={`time-slot ${selectedSlot?.start === slot.start ? 'selected' : ''}`}
                        onClick={() => handleSlotSelect(slot)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <span className="slot-time">
                          {formatTimeInCustomerTz(slot.start)}
                        </span>
                        <span className="slot-availability">
                          {slot.availableSetterCount} setter{slot.availableSetterCount !== 1 ? 's' : ''} available
                        </span>
                      </motion.button>
                    ))}
                  </div>

                  {selectedSlot && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="booking-confirmation"
                    >
                      <div className="confirmation-details">
                        <h4>Confirm Your Appointment</h4>
                        <div className="detail-row">
                          <span className="label">Date:</span>
                          <span className="value">{format(selectedDate, 'MMMM d, yyyy')}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Time:</span>
                          <span className="value">{formatTimeInCustomerTz(selectedSlot.start)}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Calendar:</span>
                          <span className="value">{selectedCalendar.title}</span>
                        </div>
                        <div className="info-box">
                          <p>📱 An admin will assign a setter and send confirmation details shortly.</p>
                        </div>
                      </div>
                      <motion.button
                        className="confirm-booking-button"
                        onClick={handleBooking}
                        disabled={loading}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {loading ? 'Booking...' : 'Confirm Appointment'}
                      </motion.button>
                    </motion.div>
                  )}
                </>
              )}
            </motion.div>
          )}
        </motion.div>
      )}
    </div>
  );
}

export default BookingCalendar;